/**
 * Debug Badge Component for FirstDistro SDK
 * 
 * Tiny indicator in bottom-right corner showing SDK status and event count.
 * Click to open debug overlay.
 */

import { getBadgeStyles } from './debug-styles';
import { getStatusIcon, type DebugStatus } from './debug-utils';

export interface DebugBadgeOptions {
  onBadgeClick?: () => void;
}

/**
 * DebugBadge class
 * Renders a small badge showing SDK status and event count
 */
export class DebugBadge {
  private container: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private badge: HTMLElement | null = null;
  private statusElement: HTMLElement | null = null;
  private countElement: HTMLElement | null = null;
  private options: DebugBadgeOptions;
  private currentCount: number = 0;
  private currentStatus: DebugStatus = 'loading';

  constructor(options: DebugBadgeOptions = {}) {
    this.options = options;
  }

  /**
   * Render the badge to the DOM
   */
  render(): void {
    if (typeof window === 'undefined') return;
    if (this.container) return; // Already rendered

    // Create container element
    this.container = document.createElement('div');
    this.container.id = 'fd-debug-badge-container';
    
    // Create shadow root for style isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    
    // Add styles
    const styleElement = document.createElement('style');
    styleElement.textContent = getBadgeStyles();
    this.shadowRoot.appendChild(styleElement);
    
    // Create badge element
    this.badge = document.createElement('div');
    this.badge.className = 'fd-badge';
    this.badge.innerHTML = `
      <span class="fd-badge-text">FD</span>
      <span class="fd-badge-status">${getStatusIcon(this.currentStatus)}</span>
      <span class="fd-badge-count">${this.currentCount}</span>
    `;
    
    // Store references to dynamic elements
    this.statusElement = this.badge.querySelector('.fd-badge-status');
    this.countElement = this.badge.querySelector('.fd-badge-count');
    
    // Add click handler
    this.badge.addEventListener('click', this.handleClick.bind(this));
    
    // Add to shadow root
    this.shadowRoot.appendChild(this.badge);
    
    // Add to document body
    document.body.appendChild(this.container);
  }

  /**
   * Handle badge click
   */
  private handleClick(): void {
    if (this.options.onBadgeClick) {
      this.options.onBadgeClick();
    }
  }

  /**
   * Update badge with new status and count
   */
  update(count: number, status: DebugStatus): void {
    this.currentCount = count;
    this.currentStatus = status;
    
    if (this.statusElement) {
      this.statusElement.textContent = getStatusIcon(status);
    }
    
    if (this.countElement) {
      this.countElement.textContent = String(count);
    }
  }

  /**
   * Update event count only
   */
  updateCount(count: number): void {
    this.currentCount = count;
    
    if (this.countElement) {
      this.countElement.textContent = String(count);
    }
  }

  /**
   * Update status only
   */
  updateStatus(status: DebugStatus): void {
    this.currentStatus = status;
    
    if (this.statusElement) {
      this.statusElement.textContent = getStatusIcon(status);
    }
  }

  /**
   * Get current event count
   */
  getCount(): number {
    return this.currentCount;
  }

  /**
   * Get current status
   */
  getStatus(): DebugStatus {
    return this.currentStatus;
  }

  /**
   * Check if badge is rendered
   */
  isRendered(): boolean {
    return this.container !== null;
  }

  /**
   * Remove badge from DOM
   */
  destroy(): void {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    
    this.container = null;
    this.shadowRoot = null;
    this.badge = null;
    this.statusElement = null;
    this.countElement = null;
  }
}
